import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Paper,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  Button,
  LinearProgress,
  Avatar,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Badge,
  Stack,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  People,
  Work,
  Assignment,
  TrendingUp,
  TrendingDown,
  Visibility,
  PersonAdd,
  AssignmentTurnedIn,
  Schedule,
  CheckCircle,
  Warning,
  Business,
  LocationOn,
  ArrowForward,
  VideoCall,
  CalendarToday,
  Timer,
  Assessment,
  Notifications,
  Dashboard,
  Settings,
  Analytics,
  Refresh,
  Download,
  FilterList,
  MoreVert,
  Star,
  StarBorder,
  TrendingFlat,
  AccessTime,
  CheckCircleOutline,
  Cancel,
  Pending,
  PlayArrow,
  Stop,
  Edit,
  Delete,
  Email,
  Phone,
  LinkedIn,
  GitHub,
  Language,
  School,
  Psychology,
  Engineering,
  Code,
  BarChart,
  PieChart,
  ShowChart,
  Timeline,
  Assessment as AssessmentIcon,
  Speed,
  Visibility as VisibilityIcon,
  Group,
  BusinessCenter,
  AttachMoney,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const AnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [timeRange, setTimeRange] = useState('30');
  const [activeTab, setActiveTab] = useState(0);

  // Dashboard Data States
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalJobs: 0,
    activeJobs: 0,
    totalApplications: 0,
    pendingApplications: 0,
    totalSubmissions: 0,
    scheduledInterviews: 0,
    completedInterviews: 0,
    hiredThisMonth: 0,
    newApplicationsToday: 0,
    conversionRate: 0,
    avgTimeToHire: 0,
    avgApplicationsPerJob: 0,
    interviewSuccessRate: 0
  });

  const [usersByRole, setUsersByRole] = useState({});
  const [applicationsByStatus, setApplicationsByStatus] = useState({});
  const [applicationsByMonth, setApplicationsByMonth] = useState([]);
  const [hiringTrends, setHiringTrends] = useState([]);
  const [departmentPerformance, setDepartmentPerformance] = useState([]);
  const [topPerformers, setTopPerformers] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [interviewMetrics, setInterviewMetrics] = useState({
    scheduled: 0,
    completed: 0,
    cancelled: 0,
    successRate: 0,
    avgDuration: 0
  });

  useEffect(() => {
    fetchDashboardData();
  }, [refreshKey, timeRange]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch all data in parallel for better performance
      const [
        adminStatsRes,
        applicationsRes,
        interviewsRes,
        jobsRes,
        usersRes
      ] = await Promise.all([
        axios.get('http://localhost:5000/api/admin/stats', { headers }),
        axios.get('http://localhost:5000/api/applications/admin/all?limit=100', { headers }),
        axios.get('http://localhost:5000/api/interviews?limit=50', { headers }),
        axios.get('http://localhost:5000/api/jobs?limit=50', { headers }),
        axios.get('http://localhost:5000/api/admin/users', { headers })
      ]);

      // Process admin stats
      const adminStats = adminStatsRes.data.data;
      const applications = applicationsRes.data.data || [];
      const interviews = interviewsRes.data.data || [];
      const jobs = jobsRes.data.data || [];
      const users = usersRes.data.data || [];

      // Calculate advanced metrics
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);
      
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      const startOfRange = new Date();
      startOfRange.setDate(startOfRange.getDate() - parseInt(timeRange));

      const hiredThisMonth = applications.filter(app => 
        app.status === 'hired' && 
        new Date(app.updatedAt) >= startOfMonth
      ).length;

      const newApplicationsToday = applications.filter(app =>
        new Date(app.createdAt) >= startOfToday
      ).length;

      const totalApplications = applications.length;
      const hiredTotal = applications.filter(app => app.status === 'hired').length;
      const conversionRate = totalApplications > 0 
        ? ((hiredTotal / totalApplications) * 100).toFixed(1)
        : 0;

      // Calculate time to hire
      const hiredApplications = applications.filter(app => app.status === 'hired');
      const avgTimeToHire = hiredApplications.length > 0 
        ? hiredApplications.reduce((sum, app) => {
            const appliedDate = new Date(app.createdAt);
            const hiredDate = new Date(app.updatedAt);
            return sum + (hiredDate - appliedDate) / (1000 * 60 * 60 * 24);
          }, 0) / hiredApplications.length
        : 0;

      // Calculate applications per job
      const avgApplicationsPerJob = jobs.length > 0 
        ? totalApplications / jobs.length 
        : 0;

      // Calculate interview success rate
      const completedInterviews = interviews.filter(interview => interview.status === 'completed');
      const successfulInterviews = completedInterviews.filter(interview => 
        interview.recommendation === 'hire' || interview.recommendation === 'strong-hire'
      );
      const interviewSuccessRate = completedInterviews.length > 0 
        ? ((successfulInterviews.length / completedInterviews.length) * 100).toFixed(1)
        : 0;

      // Generate monthly application trends
      const monthlyData = [];
      for (let i = 5; i >= 0; i--) {
        const date = new Date();
        date.setMonth(date.getMonth() - i);
        const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
        const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        
        const monthApplications = applications.filter(app => {
          const appDate = new Date(app.createdAt);
          return appDate >= monthStart && appDate <= monthEnd;
        }).length;

        const monthHires = applications.filter(app => {
          const appDate = new Date(app.updatedAt);
          return app.status === 'hired' && appDate >= monthStart && appDate <= monthEnd;
        }).length;

        monthlyData.push({
          month: date.toLocaleDateString('en-US', { month: 'short' }),
          applications: monthApplications,
          hires: monthHires
        });
      }

      // Calculate department performance
      const departmentData = {};
      jobs.forEach(job => {
        if (!departmentData[job.department]) {
          departmentData[job.department] = {
            totalJobs: 0,
            activeJobs: 0,
            applications: 0,
            hires: 0,
            avgRating: 0
          };
        }
        departmentData[job.department].totalJobs++;
        if (job.status === 'active') {
          departmentData[job.department].activeJobs++;
        }
      });

      applications.forEach(app => {
        if (app.job && app.job.department && departmentData[app.job.department]) {
          departmentData[app.job.department].applications++;
          if (app.status === 'hired') {
            departmentData[app.job.department].hires++;
          }
        }
      });

      const departmentStatsArray = Object.entries(departmentData).map(([dept, data]) => ({
        department: dept,
        ...data,
        conversionRate: data.applications > 0 ? ((data.hires / data.applications) * 100).toFixed(1) : 0
      })).sort((a, b) => b.applications - a.applications);

      // Get top performing jobs
      const topJobsData = jobs
        .filter(job => job.status === 'active')
        .map(job => ({
          ...job,
          applicationsCount: applications.filter(app => app.job?._id === job._id).length,
          hiresCount: applications.filter(app => app.job?._id === job._id && app.status === 'hired').length
        }))
        .sort((a, b) => b.applicationsCount - a.applicationsCount)
        .slice(0, 10);

      // Calculate interview metrics
      const scheduledInterviews = interviews.filter(interview => interview.status === 'scheduled').length;
      const completedInterviewsCount = interviews.filter(interview => interview.status === 'completed').length;
      const cancelledInterviews = interviews.filter(interview => interview.status === 'cancelled').length;
      
      const avgInterviewDuration = completedInterviews.length > 0 
        ? completedInterviews.reduce((sum, interview) => sum + (interview.duration || 60), 0) / completedInterviews.length
        : 60;

      setStats({
        totalUsers: adminStats.totalUsers || 0,
        totalJobs: adminStats.totalJobs || 0,
        activeJobs: adminStats.activeJobs || 0,
        totalApplications: adminStats.totalApplications || 0,
        pendingApplications: adminStats.pendingApplications || 0,
        totalSubmissions: adminStats.totalSubmissions || 0,
        scheduledInterviews,
        completedInterviews: completedInterviewsCount,
        hiredThisMonth,
        newApplicationsToday,
        conversionRate: parseFloat(conversionRate),
        avgTimeToHire: Math.round(avgTimeToHire),
        avgApplicationsPerJob: Math.round(avgApplicationsPerJob * 10) / 10,
        interviewSuccessRate: parseFloat(interviewSuccessRate)
      });

      setUsersByRole(adminStats.usersByRole || {});
      setApplicationsByStatus(adminStats.applicationsByStatus || {});
      setApplicationsByMonth(monthlyData);
      setHiringTrends(monthlyData);
      setDepartmentPerformance(departmentStatsArray);
      setTopPerformers(topJobsData);
      setRecentActivity(applications.slice(0, 10));
      setInterviewMetrics({
        scheduled: scheduledInterviews,
        completed: completedInterviewsCount,
        cancelled: cancelledInterviews,
        successRate: parseFloat(interviewSuccessRate),
        avgDuration: Math.round(avgInterviewDuration)
      });

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const getStatusColor = (status) => {
    const colors = {
      'submitted': 'info',
      'under_review': 'warning',
      'shortlisted': 'success',
      'interview_scheduled': 'primary',
      'rejected': 'error',
      'hired': 'success',
      'withdrawn': 'default',
      'active': 'success',
      'paused': 'warning',
      'closed': 'error',
      'completed': 'success',
      'pending': 'warning',
      'in_progress': 'info'
    };
    return colors[status] || 'default';
  };

  const getDepartmentIcon = (department) => {
    const icons = {
      'Engineering': <Engineering />,
      'Design': <Code />,
      'Marketing': <TrendingUp />,
      'Sales': <Business />,
      'Finance': <Business />,
      'HR': <People />,
      'Operations': <Settings />,
      'Product': <Business />,
      'Research': <School />,
      'Support': <Psychology />
    };
    return icons[department] || <Business />;
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const calculateDaysAgo = (date) => {
    const days = Math.floor((new Date() - new Date(date)) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    return `${days} days ago`;
  };

  const getTrendIcon = (current, previous) => {
    if (current > previous) return <TrendingUp color="success" />;
    if (current < previous) return <TrendingDown color="error" />;
    return <TrendingFlat color="action" />;
  };

  const getTrendPercentage = (current, previous) => {
    if (previous === 0) return current > 0 ? '+100%' : '0%';
    const change = ((current - previous) / previous) * 100;
    return change > 0 ? `+${change.toFixed(1)}%` : `${change.toFixed(1)}%`;
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" sx={{ fontWeight: 'bold', color: '#1a237e' }}>
              Analytics Dashboard
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
              Comprehensive hiring analytics and performance metrics
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Time Range</InputLabel>
              <Select
                value={timeRange}
                label="Time Range"
                onChange={(e) => setTimeRange(e.target.value)}
              >
                <MenuItem value="7">Last 7 days</MenuItem>
                <MenuItem value="30">Last 30 days</MenuItem>
                <MenuItem value="90">Last 90 days</MenuItem>
                <MenuItem value="365">Last year</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<Refresh />}
              onClick={handleRefresh}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Download />}
              onClick={() => navigate('/admin/applications/export')}
            >
              Export Report
            </Button>
          </Box>
        </Box>
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}
      </Box>

      {/* Key Performance Indicators */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #4caf5015, #4caf5025)',
            border: '1px solid #4caf5030'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: '#4caf50' }}>
                    {stats.conversionRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Conversion Rate
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    {getTrendIcon(stats.conversionRate, 12.5)}
                    <Typography variant="caption" color="success.main">
                      {getTrendPercentage(stats.conversionRate, 12.5)} vs last month
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#4caf5020',
                  color: '#4caf50'
                }}>
                  <TrendingUp sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #2196f315, #2196f325)',
            border: '1px solid #2196f330'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: '#2196f3' }}>
                    {stats.avgTimeToHire}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Days to Hire
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    {getTrendIcon(45 - stats.avgTimeToHire, 15)}
                    <Typography variant="caption" color="success.main">
                      {getTrendPercentage(45 - stats.avgTimeToHire, 15)} faster
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#2196f320',
                  color: '#2196f3'
                }}>
                  <Speed sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #9c27b015, #9c27b025)',
            border: '1px solid #9c27b030'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: '#9c27b0' }}>
                    {stats.interviewSuccessRate}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Interview Success Rate
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    {getTrendIcon(stats.interviewSuccessRate, 65)}
                    <Typography variant="caption" color="success.main">
                      {getTrendPercentage(stats.interviewSuccessRate, 65)} vs target
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#9c27b020',
                  color: '#9c27b0'
                }}>
                  <CheckCircleIcon sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ 
            height: '100%', 
            background: 'linear-gradient(135deg, #ff980015, #ff980025)',
            border: '1px solid #ff980030'
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color: '#ff9800' }}>
                    {stats.avgApplicationsPerJob}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Avg Applications/Job
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                    {getTrendIcon(stats.avgApplicationsPerJob, 8)}
                    <Typography variant="caption" color="success.main">
                      {getTrendPercentage(stats.avgApplicationsPerJob, 8)} vs avg
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  bgcolor: '#ff980020',
                  color: '#ff9800'
                }}>
                  <AssessmentIcon sx={{ fontSize: 32 }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs for different analytics sections */}
      <Paper sx={{ mb: 4 }}>
        <Tabs 
          value={activeTab} 
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Overview" icon={<Dashboard />} />
          <Tab label="Hiring Trends" icon={<ShowChart />} />
          <Tab label="Department Analytics" icon={<BarChart />} />
          <Tab label="Interview Metrics" icon={<VideoCall />} />
          <Tab label="Performance Analysis" icon={<Analytics />} />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Grid container spacing={4}>
          {/* Applications Trend Chart */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
                Applications & Hiring Trends
              </Typography>
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <BarChart sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Applications Trend Chart
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Monthly application and hiring data visualization
                  </Typography>
                </Box>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Applications</TableCell>
                      <TableCell align="right">Hires</TableCell>
                      <TableCell align="right">Conversion</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {applicationsByMonth.map((row) => (
                      <TableRow key={row.month}>
                        <TableCell>{row.month}</TableCell>
                        <TableCell align="right">{row.applications}</TableCell>
                        <TableCell align="right">{row.hires}</TableCell>
                        <TableCell align="right">
                          {row.applications > 0 ? ((row.hires / row.applications) * 100).toFixed(1) : 0}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Quick Stats */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
                Quick Statistics
              </Typography>
              <Stack spacing={3}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Total Users
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {stats.totalUsers}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((stats.totalUsers / 100) * 100, 100)} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
                
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Active Jobs
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {stats.activeJobs}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((stats.activeJobs / 20) * 100, 100)} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Pending Reviews
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {stats.pendingApplications}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((stats.pendingApplications / 50) * 100, 100)} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>

                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Scheduled Interviews
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                      {stats.scheduledInterviews}
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={Math.min((stats.scheduledInterviews / 30) * 100, 100)} 
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 1 && (
        <Grid container spacing={4}>
          {/* Hiring Trends Chart */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
                Hiring Trends Analysis
              </Typography>
              <Box sx={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <ShowChart sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Hiring Trends Chart
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Visual representation of hiring patterns over time
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 2 && (
        <Grid container spacing={4}>
          {/* Department Performance */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
                Department Performance Analysis
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Department</TableCell>
                      <TableCell align="right">Total Jobs</TableCell>
                      <TableCell align="right">Active Jobs</TableCell>
                      <TableCell align="right">Applications</TableCell>
                      <TableCell align="right">Hires</TableCell>
                      <TableCell align="right">Conversion Rate</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {departmentPerformance.map((dept) => (
                      <TableRow key={dept.department}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getDepartmentIcon(dept.department)}
                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                              {dept.department}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{dept.totalJobs}</TableCell>
                        <TableCell align="right">{dept.activeJobs}</TableCell>
                        <TableCell align="right">{dept.applications}</TableCell>
                        <TableCell align="right">{dept.hires}</TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={`${dept.conversionRate}%`} 
                            size="small" 
                            color={parseFloat(dept.conversionRate) > 15 ? 'success' : 'warning'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Department Chart */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
                Department Distribution
              </Typography>
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <PieChart sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Department Pie Chart
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Job distribution across departments
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 3 && (
        <Grid container spacing={4}>
          {/* Interview Metrics */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
                Interview Performance Metrics
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {interviewMetrics.scheduled}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Scheduled
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                      {interviewMetrics.completed}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'error.main' }}>
                      {interviewMetrics.cancelled}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cancelled
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ textAlign: 'center', p: 2 }}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                      {interviewMetrics.avgDuration}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Avg Duration (min)
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Interview Success Rate
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={interviewMetrics.successRate} 
                    sx={{ flex: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {interviewMetrics.successRate}%
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Interview Timeline */}
          <Grid item xs={12} lg={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
                Interview Timeline
              </Typography>
              <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Timeline sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    Interview Timeline Chart
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Interview scheduling and completion timeline
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {activeTab === 4 && (
        <Grid container spacing={4}>
          {/* Top Performing Jobs */}
          <Grid item xs={12} lg={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
                Top Performing Jobs
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Job Title</TableCell>
                      <TableCell>Department</TableCell>
                      <TableCell align="right">Applications</TableCell>
                      <TableCell align="right">Hires</TableCell>
                      <TableCell align="right">Conversion</TableCell>
                      <TableCell align="right">Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {topPerformers.map((job) => (
                      <TableRow key={job._id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                            {job.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {job.location}
                          </Typography>
                        </TableCell>
                        <TableCell>{job.department}</TableCell>
                        <TableCell align="right">{job.applicationsCount}</TableCell>
                        <TableCell align="right">{job.hiresCount}</TableCell>
                        <TableCell align="right">
                          {job.applicationsCount > 0 ? ((job.hiresCount / job.applicationsCount) * 100).toFixed(1) : 0}%
                        </TableCell>
                        <TableCell align="right">
                          <Chip 
                            label={job.status} 
                            size="small" 
                            color={getStatusColor(job.status)}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {/* Performance Summary */}
          <Grid item xs={12} lg={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1a237e', mb: 3 }}>
                Performance Summary
              </Typography>
              <Stack spacing={3}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Overall Conversion Rate
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {stats.conversionRate}%
                  </Typography>
                </Box>
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Average Time to Hire
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {stats.avgTimeToHire} days
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Interview Success Rate
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    {stats.interviewSuccessRate}%
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Applications per Job
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'info.main' }}>
                    {stats.avgApplicationsPerJob}
                  </Typography>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
};

export default AnalyticsDashboard;
